// Device-level receipt printing preferences (localStorage only - a laptop and an
// iPad in the same business can print through completely different printers).
//
// Two print methods exist, because a web app can only reach a printer two ways:
//   'system'    - the device's own print dialog. Covers USB, Wi-Fi/network and
//                 AirPrint printers, plus any printer with an installed driver.
//   'bluetooth' - raw ESC/POS bytes straight to a BLE thermal printer via Web
//                 Bluetooth (btPrinter.js). Chrome/Edge on Android + desktop only.
// No stored method = "auto" (the pre-2026-10-04 behavior): Bluetooth when a BLE
// printer is connected, otherwise the system dialog.

import btPrinter from "./btPrinter.js";

const METHOD_KEY = "pointbunny_print_method";
// Key kept from the Bluetooth-only era so existing devices keep their width.
const PAPER_KEY = "pointbunny_bt_paper_width";

export const normalizePaper = (v) =>
  v === "full" ? "full" : Number(v) === 58 ? 58 : 80;

const read = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const write = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* private mode etc. */ }
};

// 58 | 80 | 'full' ('full' = a normal sheet printer, receipt centered on the page).
export function getPaper() {
  const v = read(PAPER_KEY);
  return v == null ? 80 : normalizePaper(v);
}

export function setPaper(v) {
  write(PAPER_KEY, String(normalizePaper(v)));
}

// 'system' | 'bluetooth' | null (auto - never chosen on this device).
export function getMethod() {
  const v = read(METHOD_KEY);
  return v === "system" || v === "bluetooth" ? v : null;
}

export function setMethod(m) {
  if (m === "system" || m === "bluetooth") write(METHOD_KEY, m);
}

// The method a print should actually use right now. A stored 'bluetooth' on a
// browser without Web Bluetooth (e.g. the same profile opened in Safari) falls
// back to the system dialog rather than failing every print.
export function effectiveMethod(method = getMethod()) {
  if (method === "bluetooth") return btPrinter.isSupported() ? "bluetooth" : "system";
  if (method === "system") return "system";
  return btPrinter.isConnected() ? "bluetooth" : "system";
}

// Best guess for this device: Bluetooth only when the browser supports it AND a
// printer was already paired here; everything else (iPad, Safari, Firefox,
// USB/network setups) is the system dialog.
export async function suggestMethod() {
  if (!btPrinter.isSupported()) return "system";
  if (btPrinter.isConnected()) return "bluetooth";
  try {
    const devices = await navigator.bluetooth.getDevices?.();
    return devices?.length ? "bluetooth" : "system";
  } catch {
    return "system";
  }
}

// Where the receipt sits across the paper roll. Printers don't all feed or
// measure paper the same way (see the 58mm/80mm centering saga in
// pointbunny-app-todos.md), so no single CSS placement is right for every
// printer - the owner picks whichever looks right on a Test print.
//   'default' - flush left (the live-tuned placement every receipt used before)
//   'center'  - auto margins, for printers that leave a wide blank on the right
//   'left'    - nudged left, for printers that cut off the right edge
const POSITION_KEY = "pointbunny_receipt_position";

export const normalizePosition = (v) =>
  v === "center" || v === "left" ? v : "default";

export function getPosition() {
  return normalizePosition(read(POSITION_KEY));
}

export function setPosition(v) {
  write(POSITION_KEY, normalizePosition(v));
}
